/**
 * DevEngine — developer-only side-by-side comparison of the placeholder
 * reasoning engine and the LLM-backed reasoning engine.
 *
 * This is NOT a customer-facing surface. It exists so we can evaluate
 * whether the LLM engine produces specific, useful, evidence-based
 * opportunities before we build any UI around it.
 *
 * Route: /dev/engine
 */

import { useState } from "react";
import { allFixtures, type ReferenceStoreFixture } from "@/engine/harness/fixtures";
import { PlaceholderReasoningEngine } from "@/engine/reasoning/placeholder-engine";
import { LlmReasoningEngine } from "@/engine/reasoning/llm-engine";
import { scoreRun, type StoreScorecard } from "@/engine/harness/scoring";
import type { EngineOutput } from "@/engine/types";
import type { ReferenceStore } from "@/engine/reference-stores/types";
import refA from "@/engine/reference-stores/reference-store-a.json";
import refB from "@/engine/reference-stores/reference-store-b.json";
import refC from "@/engine/reference-stores/reference-store-c.json";

const refBySlug: Record<string, ReferenceStore> = {
  "reference-store-a": refA as ReferenceStore,
  "reference-store-b": refB as ReferenceStore,
  "reference-store-c": refC as ReferenceStore,
};

type RunState = {
  loading: boolean;
  error?: string;
  output?: EngineOutput;
  scorecard?: StoreScorecard;
  ms?: number;
};

const emptyRun: RunState = { loading: false };

export default function DevEngine() {
  const [fixtureSlug, setFixtureSlug] = useState<string>(allFixtures[0].slug);
  const [placeholderRun, setPlaceholderRun] = useState<RunState>(emptyRun);
  const [llmRun, setLlmRun] = useState<RunState>(emptyRun);

  const fixture: ReferenceStoreFixture =
    allFixtures.find((f) => f.slug === fixtureSlug) ?? allFixtures[0];
  const refStore = refBySlug[fixture.slug];

  async function runEngine(kind: "placeholder" | "llm") {
    const setRun = kind === "placeholder" ? setPlaceholderRun : setLlmRun;
    setRun({ loading: true });
    const engine =
      kind === "placeholder" ? new PlaceholderReasoningEngine() : new LlmReasoningEngine();
    const started = performance.now();
    try {
      const output = await engine.analyze({
        business_context: fixture.business_context,
        demand: fixture.demand,
        visibility: fixture.visibility,
        provider_versions: fixture.provider_versions,
      });
      const scorecard = scoreRun(fixture, refStore, output);
      setRun({
        loading: false,
        output,
        scorecard,
        ms: Math.round(performance.now() - started),
      });
    } catch (e) {
      setRun({
        loading: false,
        error: e instanceof Error ? e.message : String(e),
        ms: Math.round(performance.now() - started),
      });
    }
  }

  async function runBoth() {
    await Promise.all([runEngine("placeholder"), runEngine("llm")]);
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-6 md:p-10">
      <div className="mx-auto max-w-[1400px] space-y-6">
        <header className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            Retail Ranker · Developer tools
          </p>
          <h1 className="text-3xl font-semibold">Reasoning Engine — side-by-side</h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Not a customer surface. Runs the same Reference Store fixture through
            the deterministic placeholder and the LLM-backed engine so we can
            judge whether the real engine produces specific, useful,
            evidence-based opportunities.
          </p>
        </header>

        <section className="rounded-lg border border-border bg-card p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium">Reference store:</label>
            <select
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={fixtureSlug}
              onChange={(e) => {
                setFixtureSlug(e.target.value);
                setPlaceholderRun(emptyRun);
                setLlmRun(emptyRun);
              }}
            >
              {allFixtures.map((f) => (
                <option key={f.slug} value={f.slug}>
                  {f.slug}
                </option>
              ))}
            </select>

            <button
              onClick={() => runEngine("placeholder")}
              disabled={placeholderRun.loading}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
            >
              {placeholderRun.loading ? "Running…" : "Run placeholder"}
            </button>
            <button
              onClick={() => runEngine("llm")}
              disabled={llmRun.loading}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-accent disabled:opacity-50"
            >
              {llmRun.loading ? "Running LLM…" : "Run LLM"}
            </button>
            <button
              onClick={runBoth}
              disabled={placeholderRun.loading || llmRun.loading}
              className="rounded-md bg-primary text-primary-foreground px-3 py-2 text-sm hover:opacity-90 disabled:opacity-50"
            >
              Run both
            </button>
          </div>

          <ContextSummary fixture={fixture} />
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <EngineColumn title="Placeholder engine" subtitle="deterministic · proves loop" run={placeholderRun} />
          <EngineColumn title="LLM engine" subtitle="google/gemini-3-flash-preview" run={llmRun} />
        </div>
      </div>
    </div>
  );
}

function ContextSummary({ fixture }: { fixture: ReferenceStoreFixture }) {
  const ctx = fixture.business_context;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
      <ContextList label="Strengths" items={ctx.strengths} />
      <ContextList label="Goals" items={ctx.goals} />
      <ContextList label="Constraints" items={ctx.constraints} />
      <ContextList label={`Demand signals (${fixture.demand.length})`} items={fixture.demand.map((d) => `${d.term} · vol ${d.monthly_volume ?? d.volume_index ?? "?"} · ${d.trend ?? "flat"}`)} />
      <ContextList label={`Visibility signals (${fixture.visibility.length})`} items={fixture.visibility.map((v) => `${v.term} · ${v.presence}${v.detail ? ` — ${v.detail}` : ""}`)} />
    </div>
  );
}

function ContextList({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="space-y-1">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <ul className="space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-foreground/80">· {it}</li>
        ))}
        {items.length === 0 && <li className="italic text-muted-foreground">none</li>}
      </ul>
    </div>
  );
}

function EngineColumn({ title, subtitle, run }: { title: string; subtitle: string; run: RunState }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-4 min-h-[400px]">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {run.ms !== undefined && (
          <span className="text-xs text-muted-foreground">{run.ms} ms</span>
        )}
      </div>

      {run.loading && <p className="text-sm text-muted-foreground">Running…</p>}
      {run.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive whitespace-pre-wrap">
          {run.error}
        </div>
      )}

      {run.scorecard && (
        <div className="rounded-md border border-border p-3">
          <p className="text-sm font-medium mb-2">
            Scorecard: {run.scorecard.passed}/{run.scorecard.total}
          </p>
          <ul className="space-y-1 text-xs">
            {run.scorecard.scores.map((s) => (
              <li key={s.capability} className="flex gap-2">
                <span className={s.passed ? "text-emerald-600" : "text-destructive"}>
                  {s.passed ? "✓" : "✗"}
                </span>
                <span className="font-mono">{s.capability}</span>
                <span className="text-muted-foreground">— {s.note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {run.output && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">engine: {run.output.engine_version}</p>

          {run.output.opportunities.length === 0 && (
            <p className="text-sm italic text-muted-foreground">No opportunities returned.</p>
          )}

          {run.output.opportunities.map((o, i) => (
            <details key={i} open={i < 2} className="rounded-md border border-border p-3">
              <summary className="cursor-pointer text-sm font-medium">
                #{o.priority_rank ?? i + 1} · {o.title}{" "}
                <span className="text-xs text-muted-foreground">
                  ({o.horizon} · impact {o.impact} · effort {o.effort} · conf {o.confidence})
                </span>
              </summary>
              <div className="mt-2 space-y-2 text-sm">
                <Field label="What we found" value={o.what_we_found} />
                <Field label="Why it matters" value={o.why_it_matters} />
                <Field label="Recommended next move" value={o.recommended_next_move} />
                <Field label="Estimated business outcome" value={o.estimated_business_outcome ?? "—"} />
                <Field label="Affects" value={(o.affects ?? []).join(", ") || "—"} />
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Evidence ({(o.evidence ?? []).length})</summary>
                  <pre className="mt-1 overflow-x-auto bg-muted/50 p-2 rounded">
{JSON.stringify(o.evidence ?? [], null, 2)}
                  </pre>
                </details>
              </div>
            </details>
          ))}

          <details className="rounded-md border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Deliberation ({(run.output.deliberation?.considered ?? []).length} considered,{" "}
              {(run.output.deliberation?.rejected ?? []).length} rejected)
            </summary>
            <div className="mt-2 space-y-3 text-sm">
              {run.output.deliberation?.ranked_first_because && (
                <p><span className="font-medium">Ranked first because:</span> {run.output.deliberation.ranked_first_because}</p>
              )}
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Considered</p>
                <ul className="space-y-0.5 text-xs">
                  {(run.output.deliberation?.considered ?? []).map((c, i) => (
                    <li key={i}>· <span className="font-medium">{c.title}</span>{c.note ? ` — ${c.note}` : ""}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Rejected</p>
                <ul className="space-y-0.5 text-xs">
                  {(run.output.deliberation?.rejected ?? []).map((c, i) => (
                    <li key={i}>· <span className="font-medium">{c.title}</span> — {c.reason}</li>
                  ))}
                  {(run.output.deliberation?.rejected ?? []).length === 0 && (
                    <li className="italic text-muted-foreground">none</li>
                  )}
                </ul>
              </div>
            </div>
          </details>

          {run.output.insufficient_evidence.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
              <p className="text-sm font-medium mb-1">Insufficient evidence</p>
              <ul className="space-y-1 text-xs">
                {run.output.insufficient_evidence.map((n, i) => (
                  <li key={i}>
                    · <span className="font-medium">{n.area}</span> — {n.reason}
                    {n.what_would_unblock_it && (
                      <span className="text-muted-foreground"> (unblock: {n.what_would_unblock_it})</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-foreground/90">{value}</p>
    </div>
  );
}
